import { useState } from 'react';
import { SocialMedia } from '../../../api/user';
import useAppContext from '../../../hooks/useAppContext';
import useUserMutation from '../../SignUp/hooks/useUserMutation';
import useUploadProfileImageMutation from '../../hooks/useUploadProfileImageMutation';
import useUploadArtistsCoverMutation from '../../hooks/useUploadArtistsCoverMutation';
import { AlertType } from '../../../design-system/DSAlert';
import { EDIT_ARTIST_IDENTITY_ALERT } from '../../../consts/alerts';
import { MAX_IMAGE_SIZE_BYTES } from '../../../consts/general';
import { SocialMediaTypes } from '../../../types/socialMedia';
import {
  isValidSocialLink,
  checkWebsiteReachable,
} from '../../../utils/socialMedia';

export type EditArtistIdentityState = {
  isEditing: boolean;
  showValidationErrors: boolean;
  requireAllFields: boolean;
  handleStartEditing: (options?: {
    showErrors?: boolean;
    requireAllFields?: boolean;
  }) => void;
  handleCancel: () => void;
  handleSave: () => void;
  editName: string;
  setEditName: (value: string) => void;
  editShortDescription: string;
  setEditShortDescription: (value: string) => void;
  editDescription: string;
  setEditDescription: (value: string) => void;
  editCountry: string;
  setEditCountry: (value: string) => void;
  editPhotoUrl: string;
  editCoverUrl: string;
  editSocialMedia: SocialMedia[];
  setEditSocialMedia: (value: SocialMedia[]) => void;
  handleCoverUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleProfilePhotoUpload: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  disabledSaveButton: boolean;
  isUploadingCover: boolean;
  isUploadingPhoto: boolean;
  isSaving: boolean;
  unreachableWebsite: string | null;
};

const useEditArtistIdentity = (): EditArtistIdentityState => {
  const { user, setUser, showAlert, isAdmin } = useAppContext();
  const isArtist = user?.type === 'Artist';

  const [isEditing, setIsEditing] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [forceRequireAllFields, setForceRequireAllFields] = useState(false);
  const [editName, setEditName] = useState('');
  const [editShortDescription, setEditShortDescription] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editCountry, setEditCountry] = useState('');
  const [editPhotoUrl, setEditPhotoUrl] = useState('');
  const [editCoverUrl, setEditCoverUrl] = useState('');
  const [editSocialMedia, setEditSocialMedia] = useState<SocialMedia[]>([]);
  const [unreachableWebsite, setUnreachableWebsite] = useState<string | null>(
    null,
  );

  const [isValidatingWebsite, setIsValidatingWebsite] = useState(false);

  const { mutate: updateUser, isPending: isSaving } = useUserMutation(
    user?.id,
    {
      optimistic: false,
      onSuccess: (updatedData) => {
        setUser((previousUser) => ({
          ...previousUser,
          ...updatedData,
        }));
        showAlert({
          message: EDIT_ARTIST_IDENTITY_ALERT,
          alertType: AlertType.SUCCESS,
        });
        setIsEditing(false);
      },
      onError: () => {
        showAlert({
          message: 'Failed to save profile changes. Please try again.',
          alertType: AlertType.ERROR,
        });
      },
    },
  );

  const { mutate: uploadProfileImage, isPending: isUploadingPhoto } =
    useUploadProfileImageMutation({
      onSuccess: (response) => {
        setEditPhotoUrl(response.url);
      },
      onError: () => {
        setEditPhotoUrl(user?.photoUrl || '');
      },
    });

  const { mutate: uploadCoverImage, isPending: isUploadingCover } =
    useUploadArtistsCoverMutation({
      onSuccess: (response) => {
        setEditCoverUrl(response.url);
      },
      onError: () => {
        setEditCoverUrl(user?.artistProfile?.coverUrl || '');
      },
    });

  const handleStartEditing = (options?: {
    showErrors?: boolean;
    requireAllFields?: boolean;
  }) => {
    setEditName(user?.name || '');
    setEditShortDescription(user?.title || '');
    setEditDescription(user?.description || '');
    setEditCountry(user?.country || '');
    setEditPhotoUrl(user?.photoUrl || '');
    setEditCoverUrl(user?.artistProfile?.coverUrl || '');
    setEditSocialMedia(user?.socialMedia || []);
    setShowValidationErrors(options?.showErrors ?? false);
    setForceRequireAllFields(options?.requireAllFields ?? false);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setShowValidationErrors(false);
  };

  // Reject oversized images before uploading. The backend fetches the file
  // into Cloudinary, which hard-caps fetched resources at 40MB and 500s past
  // it; we enforce the studio's stricter 10MB image limit up front instead.
  const isImageWithinSizeLimit = (
    file: File,
    event: React.ChangeEvent<HTMLInputElement>,
  ): boolean => {
    if (file.size <= MAX_IMAGE_SIZE_BYTES) return true;
    showAlert({
      message: `File size exceeds the limit. Max size is ${
        MAX_IMAGE_SIZE_BYTES / 1_000_000
      }MB.`,
      alertType: AlertType.ERROR,
    });
    event.target.value = '';
    return false;
  };

  const handleCoverUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!isImageWithinSizeLimit(file, event)) return;

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setEditCoverUrl(base64);
      uploadCoverImage({ file: base64, fileName: file.name });
    };
    reader.readAsDataURL(file);
  };

  const handleProfilePhotoUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!isImageWithinSizeLimit(file, event)) return;

    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setEditPhotoUrl(base64);
      uploadProfileImage({ file: base64 });
    };
    reader.readAsDataURL(file);
  };

  const requireAllFields = forceRequireAllFields || (isArtist && !isAdmin);

  const hasMissingRequiredFields =
    requireAllFields &&
    (!editName.trim() ||
      !editDescription.trim() ||
      !editCountry ||
      !editPhotoUrl);

  const hasInvalidSocialLink = editSocialMedia.some(
    (item) =>
      !!item.link?.trim() && !isValidSocialLink(item.network, item.link),
  );

  const disabledSaveButton =
    hasMissingRequiredFields ||
    isUploadingCover ||
    isUploadingPhoto ||
    isValidatingWebsite ||
    hasInvalidSocialLink;

  const handleSave = async () => {
    if (disabledSaveButton) return;

    const websiteLink = editSocialMedia
      .find((item) => item.network === SocialMediaTypes.WEBSITE)
      ?.link?.trim();

    if (
      websiteLink &&
      isValidSocialLink(SocialMediaTypes.WEBSITE, websiteLink)
    ) {
      setIsValidatingWebsite(true);
      const reachable = await checkWebsiteReachable(websiteLink);
      setIsValidatingWebsite(false);
      if (!reachable) {
        setUnreachableWebsite(websiteLink);
        showAlert({
          message:
            'That website link could not be reached. Please check it before saving.',
          alertType: AlertType.ERROR,
        });
        return;
      }
    }

    setUnreachableWebsite(null);
    updateUser({
      name: editName,
      title: editShortDescription,
      description: editDescription,
      country: editCountry,
      photoUrl: editPhotoUrl,
      artistProfile: {
        customSections: [],
        ...user?.artistProfile,
        coverUrl: editCoverUrl,
      },
      socialMedia: editSocialMedia,
    });
  };

  return {
    isEditing,
    showValidationErrors,
    requireAllFields,
    handleStartEditing,
    handleCancel,
    handleSave,
    editName,
    setEditName,
    editShortDescription,
    setEditShortDescription,
    editDescription,
    setEditDescription,
    editCountry,
    setEditCountry,
    editPhotoUrl,
    editCoverUrl,
    editSocialMedia,
    setEditSocialMedia,
    handleCoverUpload,
    handleProfilePhotoUpload,
    disabledSaveButton,
    isUploadingCover,
    isUploadingPhoto,
    isSaving,
    unreachableWebsite,
  };
};

export default useEditArtistIdentity;
